/**
 * Here for Women Homepage JavaScript
 * Wrapped in IIFE to avoid global namespace pollution
 * Works with Shopify/Shrine theme
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  document.addEventListener('DOMContentLoaded', function() {
    initHFWHomepage();
  });

  function initHFWHomepage() {
    // Initialize all components
    initReviewsCarousel();
    initBestsellersCarousel();
    initVideoTestimonials();
    initFAQAccordion();
    initStatsCounter();
    initHeaderFunctions();
    initAddToCartForms();
    updateCartCount();
  }

  /**
   * Header Functions
   */
  function initHeaderFunctions() {
    // Initialize cart count on page load
    updateCartCount();
  }

  /**
   * Add to Cart Forms
   * Intercept form submissions to update cart count
   */
  function initAddToCartForms() {
    const forms = document.querySelectorAll('.hfw-add-to-cart-form');

    forms.forEach((form, index) => {
      // Flag to prevent double-triggering
      let isProcessing = false;

      // Handler function that both listeners will use
      const handleAddToCart = async function(e) {
        if (isProcessing) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        isProcessing = true;

        const submitButton = form.querySelector('button[type="submit"]');
        const variantIdInput = form.querySelector('input[name="id"]');
        const variantId = variantIdInput ? variantIdInput.value : null;

        if (!variantId || !submitButton) {
          console.error('HFW: Missing variant ID or submit button');
          isProcessing = false;
          return;
        }

        // Disable button and show loading state
        const originalText = submitButton.innerText;
        submitButton.disabled = true;
        submitButton.innerText = 'Adding...';

        try {
          // Use FormData for UpCart compatibility
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');

          const response = await fetch('/cart/add.js?upcart=1&opens_cart=maybe', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.description || 'Failed to add to cart');
          }

          await response.json();

          // Success - immediately increment cart count for instant feedback
          submitButton.innerText = 'Added!';
          const cartCounts = document.querySelectorAll('.hfw-cart-count');
          cartCounts.forEach((count) => {
            const currentCount = parseInt(count.textContent) || 0;
            count.textContent = currentCount + 1;
          });

          // Verify with actual cart data after a short delay
          setTimeout(() => {
            updateCartCount();
          }, 500);

          // Reset button and flag
          setTimeout(() => {
            submitButton.disabled = false;
            submitButton.innerText = originalText;
            isProcessing = false;
          }, 1500);

        } catch (error) {
          console.error('HFW: Add to cart failed:', error.message);
          submitButton.innerText = originalText;
          submitButton.disabled = false;
          isProcessing = false;
          alert('Sorry, there was an error adding this item to your cart. Please try again.');
        }
      };

      // Approach 1: Form submit with event capturing (runs first)
      form.addEventListener('submit', handleAddToCart, true);

      // Approach 2: Button click listener as backup
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.addEventListener('click', function(e) {
          // Only handle if the form submit didn't fire
          setTimeout(() => {
            if (!isProcessing) {
              handleAddToCart(e);
            }
          }, 10);
        }, true);
      }
    });
  }

  window.hfwToggleMenu = function() {
    const drawer = document.getElementById('hfwMenuDrawer');
    if (drawer) {
      drawer.classList.toggle('active');
      document.body.classList.toggle('hfw-menu-open');
    }
  };

  window.hfwCloseMenu = function() {
    const drawer = document.getElementById('hfwMenuDrawer');
    if (drawer) {
      drawer.classList.remove('active');
      document.body.classList.remove('hfw-menu-open');
    }
  };

  window.hfwOpenCart = function() {
    // UpCart handles cart drawer automatically
    // Try various UpCart methods if available
    if (window.UpCart && window.UpCart.open) {
      window.UpCart.open();
    } else if (window.UC && window.UC.cart && window.UC.cart.open) {
      window.UC.cart.open();
    } else if (window.Shopify && window.Shopify.theme && window.Shopify.theme.cart) {
      // Fallback to Shopify's cart drawer if available
      window.Shopify.theme.cart.open();
    } else if (document.querySelector('cart-drawer')) {
      // Try to open Shrine's cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.open) {
        cartDrawer.open();
      }
    }
    // If no cart drawer method found, UpCart should handle it automatically
  };

  /**
   * Add Product by Handle - Fetches first available variant
   */
  window.hfwAddProductByHandle = async function(button, productHandle) {
    if (!productHandle) {
      console.error('HFW: No product handle provided');
      return;
    }

    console.log('HFW: Fetching product by handle:', productHandle);

    // Disable button and show loading state
    const originalText = button.innerText;
    button.disabled = true;
    button.innerText = 'Loading...';
    button.classList.add('hfw-loading');

    try {
      // Fetch product data
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        throw new Error('Product not found');
      }

      const product = await response.json();
      console.log('HFW: Product found:', product.title);

      // Get first available variant
      const availableVariant = product.variants.find(v => v.available) || product.variants[0];

      if (!availableVariant) {
        throw new Error('No variants available');
      }

      console.log('HFW: Using variant:', availableVariant.id, availableVariant.title);

      // Add to cart with the variant ID
      button.innerText = 'Adding...';
      await hfwAddToCart(button, availableVariant.id);

    } catch (error) {
      console.error('HFW: Error fetching product:', error.message);
      button.innerText = 'Error';
      button.classList.add('hfw-error');
      button.classList.remove('hfw-loading');

      setTimeout(() => {
        button.disabled = false;
        button.innerText = originalText;
        button.classList.remove('hfw-error');
      }, 2000);
    }
  };

  /**
   * Add to Cart - Minimal version that works with UpCart
   */
  window.hfwAddToCart = async function(button, variantId) {
    if (!variantId) {
      console.error('HFW: No variant ID provided');
      return;
    }

    console.log('HFW: Adding variant:', variantId);

    // Disable button
    const originalText = button.innerText;
    button.disabled = true;
    button.innerText = 'Adding...';

    try {
      // Use FormData exactly like the working page
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      // Add UpCart parameters to URL like the working page
      const response = await fetch('/cart/add.js?upcart=1&opens_cart=maybe', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Product not available');
      }

      // Success - let UpCart handle the UI
      button.innerText = 'Added!';

      // Update cart count if function exists
      if (typeof updateCartCount === 'function') {
        updateCartCount();
      }

      // Reset button
      setTimeout(() => {
        button.disabled = false;
        button.innerText = originalText;
      }, 1500);

    } catch (error) {
      console.error('HFW: Add to cart failed:', error.message);
      button.innerText = originalText;
      button.disabled = false;

      // Try redirecting to product page as fallback
      const fallbackUrl = button.getAttribute('data-product-url');
      if (fallbackUrl) {
        window.location.href = fallbackUrl;
      }
    }
  };

  /**
   * Reviews Carousel
   */
  let currentReview = 0;
  const reviewsTrack = document.getElementById('hfw-reviews-track');

  function initReviewsCarousel() {
    if (!reviewsTrack) return;

    const reviewCards = reviewsTrack.querySelectorAll('.hfw-review-card');
    const totalReviews = Math.max(1, reviewCards.length - 2); // Accounting for visible reviews

    // Auto-scroll reviews
    if (totalReviews > 0) {
      setInterval(() => {
        hfwNextReview();
      }, 6000);
    }

    // Add touch/swipe support for mobile
    let touchStartX = 0;
    let touchEndX = 0;

    reviewsTrack.addEventListener('touchstart', function(e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    reviewsTrack.addEventListener('touchend', function(e) {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    }, { passive: true });

    function handleSwipe() {
      const swipeThreshold = 50;
      if (touchEndX < touchStartX - swipeThreshold) {
        hfwNextReview();
      }
      if (touchEndX > touchStartX + swipeThreshold) {
        hfwPreviousReview();
      }
    }
  }

  window.hfwGoToReview = function(index) {
    if (!reviewsTrack) return;
    currentReview = index;
    const offset = -index * 33.333;
    reviewsTrack.style.transform = `translateX(${offset}%)`;
  };

  window.hfwPreviousReview = function() {
    if (!reviewsTrack) return;
    const reviewCards = reviewsTrack.querySelectorAll('.hfw-review-card');

    // Mobile: scroll one card at a time
    if (window.innerWidth <= 768) {
      const totalReviews = reviewCards.length;
      currentReview = currentReview > 0 ? currentReview - 1 : totalReviews - 1;
      const offset = -currentReview * 100;
      reviewsTrack.style.transform = `translateX(${offset}%)`;
    } else {
      // Desktop: original logic
      const totalReviews = Math.max(1, reviewCards.length - 2);
      currentReview = currentReview > 0 ? currentReview - 1 : totalReviews - 1;
      hfwGoToReview(currentReview);
    }
  };

  window.hfwNextReview = function() {
    if (!reviewsTrack) return;
    const reviewCards = reviewsTrack.querySelectorAll('.hfw-review-card');

    // Mobile: scroll one card at a time
    if (window.innerWidth <= 768) {
      const totalReviews = reviewCards.length;
      currentReview = (currentReview + 1) % totalReviews;
      const offset = -currentReview * 100;
      reviewsTrack.style.transform = `translateX(${offset}%)`;
    } else {
      // Desktop: original logic
      const totalReviews = Math.max(1, reviewCards.length - 2);
      currentReview = (currentReview + 1) % totalReviews;
      hfwGoToReview(currentReview);
    }
  };

  /**
   * Bestsellers Carousel (Mobile Only)
   */
  let currentProduct = 0;

  function initBestsellersCarousel() {
    // Only initialize on mobile
    if (window.innerWidth > 768) return;

    const productsGrid = document.querySelector('.hfw-product-grid');
    if (!productsGrid) return;

    // Track current scroll position for button navigation
    productsGrid.addEventListener('scroll', function() {
      const cardWidth = productsGrid.querySelector('.hfw-product-card').offsetWidth;
      const scrollPosition = productsGrid.scrollLeft;
      currentProduct = Math.round(scrollPosition / cardWidth);
    }, { passive: true });
  }

  window.hfwPreviousProduct = function() {
    if (window.innerWidth > 768) return;

    const productsGrid = document.querySelector('.hfw-product-grid');
    if (!productsGrid) return;

    const productCards = productsGrid.querySelectorAll('.hfw-product-card');
    const totalProducts = productCards.length;

    if (totalProducts === 0) return;

    currentProduct = currentProduct > 0 ? currentProduct - 1 : totalProducts - 1;

    // Scroll to the product
    const cardWidth = productCards[0].offsetWidth + 15; // width + gap
    productsGrid.scrollTo({
      left: currentProduct * cardWidth,
      behavior: 'smooth'
    });
  };

  window.hfwNextProduct = function() {
    if (window.innerWidth > 768) return;

    const productsGrid = document.querySelector('.hfw-product-grid');
    if (!productsGrid) return;

    const productCards = productsGrid.querySelectorAll('.hfw-product-card');
    const totalProducts = productCards.length;

    if (totalProducts === 0) return;

    currentProduct = (currentProduct + 1) % totalProducts;

    // Scroll to the product
    const cardWidth = productCards[0].offsetWidth + 15; // width + gap
    productsGrid.scrollTo({
      left: currentProduct * cardWidth,
      behavior: 'smooth'
    });
  };

  /**
   * Flip Card Functionality
   */
  window.hfwFlipCard = function(card) {
    if (!card) return;
    card.classList.toggle('flipped');
  };

  /**
   * Video Testimonials
   */
  function initVideoTestimonials() {
    // Click handlers are already in HTML onclick attributes
    // Just ensure videos can be played/paused properly
  }

  window.hfwPlayVideo = function(tile) {
    if (!tile) return;

    const video = tile.querySelector('.hfw-video-preview');
    if (!video) return;

    if (video.paused) {
      // Pause all other videos
      document.querySelectorAll('.hfw-video-preview').forEach(v => {
        if (v !== video) {
          v.pause();
          v.closest('.hfw-video-tile').classList.remove('playing');
        }
      });

      video.play();
      tile.classList.add('playing');
    } else {
      video.pause();
      tile.classList.remove('playing');
    }
  };

  /**
   * FAQ Accordion
   */
  function initFAQAccordion() {
    // Don't add event listeners since onclick is already in HTML
    // Just ensure the function is available globally
  }

  window.hfwToggleFAQ = function(button) {
    if (!button) return;

    const answer = button.nextElementSibling;
    const isActive = button.classList.contains('active');

    // Close all FAQs
    document.querySelectorAll('.hfw-faq-question').forEach(q => {
      q.classList.remove('active');
      const a = q.nextElementSibling;
      if (a && a.classList.contains('hfw-faq-answer')) {
        a.classList.remove('active');
      }
    });

    // Open clicked FAQ if it wasn't already open
    if (!isActive) {
      button.classList.add('active');
      if (answer && answer.classList.contains('hfw-faq-answer')) {
        answer.classList.add('active');
      }
    }
  };

  /**
   * Stats Counter Animation
   */
  function initStatsCounter() {
    const counters = document.querySelectorAll('.hfw-stat-number');

    if (!counters.length) return;

    try {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target) {
            animateCounter(entry.target);
            observer.unobserve(entry.target);
          }
        });
      });

      counters.forEach(counter => {
        if (counter) {
          observer.observe(counter);
        }
      });
    } catch (e) {
      // IntersectionObserver not supported or error
      counters.forEach(counter => {
        if (counter) {
          animateCounter(counter);
        }
      });
    }
  }

  function animateCounter(counter) {
    const target = counter.getAttribute('data-count');
    if (!target) return;

    // Simply set the final value without animation to avoid MutationObserver issues
    counter.textContent = target;
  }

  /**
   * Update Cart Count
   * Fetches current cart and updates all cart count elements
   */
  function updateCartCount() {
    try {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartCounts = document.querySelectorAll('.hfw-cart-count');
          cartCounts.forEach((count) => {
            if (count) {
              count.textContent = cart.item_count;
            }
          });
        })
        .catch(error => {
          console.error('HFW: Error fetching cart:', error);
        });
    } catch (e) {
      console.error('HFW: Exception in updateCartCount:', e);
    }
  }

  /**
   * Smooth Scroll for CTAs
   */
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });

  /**
   * Pause animations on reduced motion preference
   */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('.hfw-benefits-track').forEach(track => {
      track.style.animationPlayState = 'paused';
    });
  }

})();